/**
 * Request throttle for candleSnapshot (#874): Hyperliquid rate-limits /info by weight per IP (candleSnapshot costs
 * 20 + 1 per 60 candles). Volume candle requests run one at a time, at least `gapMs` apart, so quick navigation
 * between markets can never burst. Aborted waiters leave the queue without running.
 */
import { abortError } from '../hl/info';

export interface Throttle {
  run<T>(task: () => Promise<T>, signal?: AbortSignal): Promise<T>;
}

export function createThrottle(gapMs: number, now: () => number = Date.now): Throttle {
  let tail: Promise<void> = Promise.resolve();
  let lastStart = -Infinity;
  const wait = (ms: number, signal?: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
      if (signal?.aborted) return reject(abortError());
      if (ms <= 0) return resolve();
      const id = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      const onAbort = () => {
        clearTimeout(id);
        reject(abortError());
      };
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  return {
    run<T>(task: () => Promise<T>, signal?: AbortSignal): Promise<T> {
      const result = tail.then(async () => {
        if (signal?.aborted) throw abortError();
        await wait(lastStart + gapMs - now(), signal);
        lastStart = now();
        return task();
      });
      // The next task waits for this one to settle, success or not.
      tail = result.then(
        () => undefined,
        () => undefined,
      );
      return result;
    },
  };
}

/** App-wide candle throttle: one request in flight, >= 500 ms between starts. */
export const candleThrottle = createThrottle(500);
